import { Trans, useLingui } from "@lingui/react/macro";
import { Bar, Doughnut, Line } from "react-chartjs-2";

import Cover from "@/containers/Episode/partials/Cover";
import InfoLink from "@/containers/InfoLink";
import { cx } from "@/utils";
import { SENTIMENT, SENTIMENT_OPTIONS } from "@/utils/sentimentOptions";
import { truncate } from "@/utils/truncate";
import { SourceDisplay } from "@components/DataDisplay";
import Table from "@components/Table";
import Tooltip from "@components/Tooltip";
import Typography from "@components/Typography";

import { BaseWidget } from "../BaseWidget";
import {
  barOptions,
  eventPropsForPie,
  eventPropsForTimeline,
  lineOptions,
  pieOptions,
} from "../chart_options";
import { CountText } from "../CountText";
import {
  getLabelsAndDatasetsForLineChart,
  getLabelsAndDatasetsForPieChart,
  getLabelsAndDatasetsGroupedByMediaType,
  sumArray,
} from "../helpers";

import styles from "./ReachWidget.module.css";

const hasData = (data) =>
  data &&
  data.map((d) => (d.reachPerWeekSummed || []).length).reduce(sumArray, 0) > 0;

// ------------------------------------------------------------------------------------

export const ReachInfoLink = () => {
  const { t } = useLingui();
  return (
    <InfoLink
      content={t`Samlad räckvidd visar kanalens räckvidd gånger antal avsnitt med sökorden.`}
    />
  );
};

// ------------------------------------------------------------------------------------

const ReachLineWidget = (props) => {
  const isStacked = props.chartType == "area_stacked";
  const chartData = getLabelsAndDatasetsForLineChart(
    "reachPerWeekSummed",
    hasData(props.data) ? props.data : null,
    isStacked
  );
  const options = lineOptions(isStacked, props.size);

  return (
    <BaseWidget
      {...props}
      extraHeader={<ReachInfoLink />}
      hasData={hasData(props.data)}
    >
      <Line
        data={chartData}
        options={{ ...options, ...eventPropsForTimeline(chartData, props) }}
      />
    </BaseWidget>
  );
};

// ------------------------------------------------------------------------------------

const ReachPieWidget = (props) => {
  const chartData = getLabelsAndDatasetsForPieChart(
    "reachPerWeekSummed",
    hasData(props.data) ? props.data : null,
    true // convert to percentage
  );

  const options = pieOptions();

  return (
    <BaseWidget
      {...props}
      extraHeader={<ReachInfoLink />}
      hasData={hasData(props.data)}
    >
      <Doughnut
        data={chartData}
        options={{ ...options, ...eventPropsForPie(chartData, props) }}
      />
    </BaseWidget>
  );
};

// ------------------------------------------------------------------------------------

const ReachBarWidget = (props) => {
  const isStacked = true;

  const chartData = getLabelsAndDatasetsGroupedByMediaType(
    "reachPerWeek",
    hasData(props.data) ? props.data : null
  );
  const options = barOptions(isStacked, props.size);

  return (
    <BaseWidget
      {...props}
      extraHeader={<ReachInfoLink />}
      hasData={hasData(props.data)}
    >
      <Bar
        data={chartData}
        options={{
          ...options,
          ...eventPropsForTimeline(chartData, props, "mediaType"),
        }}
      />
    </BaseWidget>
  );
};

// ------------------------------------------------------------------------------------

const ReachSumWidget = (props) => {
  const chartData = getLabelsAndDatasetsForPieChart(
    "reachPerWeekSummed",
    hasData(props.data) ? props.data : null
  );

  const sum = chartData.datasets
    .map((i) => i.data.reduce(sumArray, 0))
    .reduce(sumArray, 0);

  return (
    <BaseWidget
      {...props}
      extraHeader={<ReachInfoLink />}
      hasData={hasData(props.data)}
    >
      <CountText
        testId="reach-sum"
        count={sum}
        {...props}
        extraHeader={<ReachInfoLink />}
      />
    </BaseWidget>
  );
};

// ------------------------------------------------------------------------------------

const SentimentHeaderIcon = SENTIMENT.positive.icon;

const SentimentTooltip = ({ sentiment }) => (
  <>
    {SENTIMENT_OPTIONS.map((s) => {
      return (
        <Typography variant="body-2" margin="none" block key={s.label}>
          {s.pluralLabel}: {sentiment[s.value]}
        </Typography>
      );
    })}
  </>
);

const ReachTableWidget = (props) => {
  const { t } = useLingui();

  return (
    <BaseWidget
      {...props}
      hasData={props.data && !!props.data[0]?.data.length}
      tight
      title={t`Mest inflytelserika`}
      description={t`Källor med störst räckvidd`}
    >
      <Table tight className={styles.grid}>
        <thead>
          <tr>
            <th colSpan={2}>{t`Källa`}</th>
            <th className={styles.reach}>
              <Trans>Samlad räckvidd</Trans>
              <ReachInfoLink />
            </th>
            <th>
              <Trans>Medietyp</Trans>
            </th>
            <th>
              <SentimentHeaderIcon width={18} height={18} />
            </th>
          </tr>
        </thead>

        <tbody>
          {props.data
            ? props.data[0]?.data?.map((row, i) => {
                const sortedSentiments = Object.entries(
                  row.sentiment || {}
                ).sort((a, b) => b[1] - a[1]);
                const [sentimentName, _sentimentValue] = sortedSentiments[0];
                const SentimentIcon = SENTIMENT[sentimentName]?.icon;
                const rowCount = row.count.toLocaleString();

                return (
                  <tr
                    key={i}
                    className={styles.clickableRow}
                    onClick={() =>
                      props.fetchMentions_v2({
                        channel_name: row.channel_name,
                      })
                    }
                  >
                    <td>
                      <Cover
                        className={styles.cover}
                        dumb
                        episode={{ source: row.channel_type, cover: row.cover }}
                      />
                    </td>
                    <td>
                      <strong>
                        {row.channel_name ? (
                          truncate(row.channel_name, 50)
                        ) : (
                          <Trans>Källa saknar namn</Trans>
                        )}
                      </strong>
                      <br />
                      <Trans>Omnämnd i {rowCount} inlägg</Trans>
                    </td>
                    <td>{row.reach.toLocaleString()}</td>
                    <td>
                      <strong>
                        <SourceDisplay id={row.channel_type} />
                      </strong>
                    </td>
                    <td
                      className={cx({
                        [styles.positive]: sentimentName === "positive",
                        [styles.very_positive]:
                          sentimentName === "very_positive",
                        [styles.negative]: sentimentName === "negative",
                        [styles.very_negative]:
                          sentimentName === "very_negative",
                      })}
                    >
                      <Tooltip
                        asChild={false}
                        content={<SentimentTooltip sentiment={row.sentiment} />}
                      >
                        <SentimentIcon width={18} height={18} />
                      </Tooltip>
                    </td>
                  </tr>
                );
              })
            : null}
        </tbody>
      </Table>
    </BaseWidget>
  );
};
// ------------------------------------------------------------------------------------

const ReachWidget = (props) => {
  return props.chartType == "pie" ? (
    <ReachPieWidget {...props} />
  ) : props.chartType == "bar" ? (
    <ReachBarWidget {...props} />
  ) : props.chartType == "sum" ? (
    <ReachSumWidget {...props} />
  ) : props.chartType == "table" ? (
    <ReachTableWidget {...props} />
  ) : (
    <ReachLineWidget {...props} />
  );
};

export { ReachLineWidget, ReachPieWidget, ReachTableWidget, ReachWidget };
